import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users2, LayoutGrid, Info } from "lucide-react";
import api from "@/lib/api";
import { CATEGORY_META, PROFILE_TYPES } from "@/i18n/translations";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function MatchingAdmin() {
  const [listings, setListings] = useState([]);
  const [selected, setSelected] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/listings", { params: { status: "published" } }).then(({ data }) => {
      setListings(data);
      if (data.length) setSelected(data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api.get(`/admin/matches/listing/${selected}`).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [selected]);

  return (
    <div>
      <h1 className="mb-1 font-serif text-3xl text-anthracite">Matching (v1)</h1>
      <p className="mb-6 text-sm text-anthracite/50">Correspondances suggérées à partir des taxonomies, profils et fiches. À qualifier par l'équipe RSTI.</p>

      {listings.length === 0 ? (
        <div className="border border-dashed border-tertiary bg-white py-16 text-center text-sm text-anthracite/40" data-testid="matching-empty">
          Publiez au moins une fiche pour générer des correspondances.
        </div>
      ) : (
        <>
          <div className="mb-6">
            <label className="mb-1.5 block text-xs uppercase tracking-[0.12em] text-anthracite/50">Fiche de référence</label>
            <select value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="matching-listing-select" className="w-full max-w-lg border border-tertiary bg-white px-3 py-2.5 text-sm outline-none focus:border-champagne">
              {listings.map((l) => <option key={l.id} value={l.id}>{l.title_fr || l.title_en} — {CATEGORY_META[l.category]?.fr}</option>)}
            </select>
          </div>

          {data?.disclaimer && (
            <p className="mb-6 flex items-start gap-2 border border-champagne/20 bg-champagne/5 p-3 text-xs text-anthracite/60">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-champagne" /> {data.disclaimer}
            </p>
          )}

          {loading ? <p className="text-anthracite/40">…</p> : (
            <div className="grid gap-8 lg:grid-cols-2">
              <div>
                <h2 className="mb-3 flex items-center gap-2 font-serif text-xl text-anthracite"><Users2 className="h-5 w-5 text-champagne" /> Membres correspondants</h2>
                {(!data?.profile_matches || data.profile_matches.length === 0) ? (
                  <p className="text-sm text-anthracite/40">Aucune correspondance de profil.</p>
                ) : (
                  <div className="space-y-3" data-testid="profile-matches">
                    {data.profile_matches.map((m, i) => (
                      <div key={i} className="border border-tertiary bg-white p-4">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-anthracite">{m.member?.name || "—"}</p>
                          <span className="rounded-full bg-champagne/10 px-2.5 py-0.5 text-xs font-medium text-champagne">score {m.score}</span>
                        </div>
                        <p className="text-xs text-anthracite/50">{m.member?.email} · {PROFILE_TYPES.find((p) => p.value === m.member?.profile_type)?.fr}</p>
                        <p className="mt-1 text-xs text-anthracite/60">Raisons : {m.reasons.join(", ")}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h2 className="mb-3 flex items-center gap-2 font-serif text-xl text-anthracite"><LayoutGrid className="h-5 w-5 text-champagne" /> Fiches complémentaires</h2>
                {(!data?.listing_matches || data.listing_matches.length === 0) ? (
                  <p className="text-sm text-anthracite/40">Aucune fiche complémentaire.</p>
                ) : (
                  <div className="space-y-3" data-testid="listing-matches">
                    {data.listing_matches.map((m, i) => (
                      <Link key={i} to={`/listing/${m.listing.id}`} target="_blank" className="block border border-tertiary bg-white p-4 hover:border-champagne">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-anthracite line-clamp-1">{m.listing.title_fr || m.listing.title_en}</p>
                          <span className="shrink-0 rounded-full bg-champagne/10 px-2.5 py-0.5 text-xs font-medium text-champagne">score {m.score}</span>
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="text-xs text-anthracite/50">{CATEGORY_META[m.listing.category]?.fr}</span>
                          <VerifiedBadge level={m.listing.verification?.level} />
                        </div>
                        <p className="mt-1 text-xs text-anthracite/60">Raisons : {m.reasons.join(", ")}</p>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
